import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Backdrop,
    Bullseye,
    Button,
    Checkbox,
    Form,
    FormGroup,
    Modal,
    ModalBody,
    ModalFooter,
    ModalVariant,
    Spinner,
    TextArea,
    Toolbar,
    ToolbarContent,
    ToolbarItem,
    Tooltip
} from '@patternfly/react-core';
import { CommentIcon, EraserIcon, PencilAltIcon } from '@patternfly/react-icons';
import { Octokit } from '@octokit/rest';
import { toCanvas } from 'html-to-image';
import { FEEDBACK_REPO_NAME, FEEDBACK_REPO_OWNER } from '@app/utils/config';
import { useAuth } from './AuthContext';

// --- CONFIGURATION ---
const REPO_OWNER = FEEDBACK_REPO_OWNER;
const REPO_NAME = FEEDBACK_REPO_NAME;

export const FeedbackBalloon = () => {
    const { user } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

    // New Feature States
    const [includeScreenshot, setIncludeScreenshot] = useState(true);
    const [clientIp, setClientIp] = useState('Fetching...');
    const [isCapturing, setIsCapturing] = useState(false); // <-- NEW: Capture State

    // Drawing State
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const originalCanvasRef = useRef<HTMLCanvasElement | null>(null)
    const [isDrawing, setIsDrawing] = useState(false);
    const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);

    // Fetch IP Address silently in the background
    useEffect(() => {
        fetch('/whoami')
            .then(res => res.json())
            .then(data => setClientIp(data.ip))
            .catch(() => setClientIp('Unknown / Blocked by firewall'));
    }, []);

    // 1. Initialize & Capture
    const handleOpen = () => {
        setIncludeScreenshot(true);
        setStatus('idle');
        setIsCapturing(true); // Trigger the loading overlay

        // We MUST use a timeout here to yield to the browser's rendering engine.
        // Otherwise, the heavy 'toCanvas' operation freezes the thread before the spinner can appear!
        setTimeout(async () => {
            try {
                const screenCanvas = await toCanvas(document.body, {
                    skipFonts: true,
                    filter: (node) => {
                        // Exclude both the button AND the loading overlay from the screenshot
                        return node.id !== 'pf-feedback-button' && node.id !== 'pf-feedback-overlay';
                    }
                });

                originalCanvasRef.current = screenCanvas; 
                setIsModalOpen(true);

                setTimeout(() => {
                    if (canvasRef.current) {
                        const canvas = canvasRef.current;
                        const context = canvas.getContext('2d');

                        if (context) {
                            canvas.width = screenCanvas.width;
                            canvas.height = screenCanvas.height;
                            context.drawImage(screenCanvas, 0, 0);

                            context.lineWidth = 5;
                            context.lineCap = 'round';
                            context.strokeStyle = '#C9190B';

                            setCtx(context);
                        }
                    }
                }, 100);
            } catch (e) {
                console.error("Screenshot failed", e);
                setIsModalOpen(true);
                setIncludeScreenshot(false);
            } finally {
                setIsCapturing(false); // Hide the loading overlay when done
            }
        }, 50); // 50ms gives React just enough time to paint the Spinner
    };

    // 2. Drawing Logic
    const getCoords = (e: React.MouseEvent) => {
        if (!canvasRef.current) return { x: 0, y: 0 };
        const rect = canvasRef.current.getBoundingClientRect();
        const scaleX = canvasRef.current.width / rect.width;
        const scaleY = canvasRef.current.height / rect.height;
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY
        };
    };

    const startDrawing = (e: React.MouseEvent) => {
        if (!ctx || !includeScreenshot) return;
        setIsDrawing(true);
        const { x, y } = getCoords(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const draw = (e: React.MouseEvent) => {
        if (!isDrawing || !ctx || !includeScreenshot) return;
        const { x, y } = getCoords(e);
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = () => {
        if (!ctx) return;
        setIsDrawing(false);
        ctx.closePath();
    };

    const clearDrawing = () => {
        if (!canvasRef.current || !ctx || !originalCanvasRef.current) return;
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.drawImage(originalCanvasRef.current, 0, 0);
    };
    
    // 3. Submit to GitHub
    const handleSubmit = async () => {
        setIsSubmitting(true);
        setStatus('idle');

        try {
            const octokit = new Octokit({ 
                baseUrl: '/github-api' 
            });
            
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            let imageMarkdown = '*User opted to exclude the screenshot.*';

            if (includeScreenshot && canvasRef.current) {
                const finalImageBase64 = canvasRef.current.toDataURL('image/png').split(',')[1];
                const imagePath = `feedback-images/${timestamp}.png`;
                
                const uploadResponse = await octokit.repos.createOrUpdateFileContents({
                    owner: REPO_OWNER,
                    repo: REPO_NAME,
                    path: imagePath,
                    message: `chore: upload feedback screenshot ${timestamp}`,
                    content: finalImageBase64 as string,
                    encoding: 'base64',
                });
                
                const persistentUrl = `${uploadResponse.data.content?.html_url}?raw=true`;
                imageMarkdown = `![Screenshot](${persistentUrl})`;
            }

            const userRoles = user?.roles ? user.roles.join(', ') : 'None defined';
            const componentUrl = window.location.href;
            const browserInfo = navigator.userAgent;
            const userLang = navigator.language;

            const issueBody = `### User Feedback\n${comment}\n\n` +
                              `### Annotated Screenshot\n${imageMarkdown}\n\n` +
                              `### Added by:\n${(user?.firstName || '') + ' ' + (user?.lastName || '')}\n\n` +
                              `---\n` +
                              `### Context Data\n` +
                              `- **Component URL:** ${componentUrl}\n` +
                              `- **Browser:** ${browserInfo}\n` +
                              `- **Language:** ${userLang}\n` +
                              `- **Client IP:** ${clientIp}\n` +
                              `- **User Roles:** ${userRoles}`;

            await octokit.issues.create({
                owner: REPO_OWNER,
                repo: REPO_NAME,
                title: `Visual Feedback: ${timestamp}`,
                body: issueBody,
                labels: ['visual-feedback'],
            });

            setStatus('success');
            setComment('');
            setTimeout(() => setIsModalOpen(false), 2000);
        } catch (error) {
            console.error(error);
            setStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* FULL SCREEN CAPTURE OVERLAY */}
            {isCapturing && (
                <Backdrop id="pf-feedback-overlay" style={{ zIndex: 10000 }}>
                    <Bullseye>
                        <div style={{ textAlign: 'center', color: 'white' }}>
                            <Spinner size="xl" aria-label="Preparing screenshot" />
                            <h2 style={{ marginTop: '20px', fontWeight: 'bold' }}>Capturing your screen...</h2>
                        </div>
                    </Bullseye>
                </Backdrop>
            )}

            {/* Floating Button */}
            <div id="pf-feedback-button" style={{ position: 'fixed', bottom: '30px', left: '30px', zIndex: 9999 }}>
                <Tooltip content="Report a bug or suggest an improvement">
                    <Button variant="danger" 
                        style={{ padding: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }} 
                        onClick={handleOpen}
                    >
                        <CommentIcon />
                    </Button>
                </Tooltip>
            </div>

            {/* Modal */}
            <Modal
                variant={ModalVariant.large}
                title="Annotate & Send Feedback"
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            >
                <ModalBody>
                    <div style={{ display: 'flex', gap: '20px', flexDirection: 'column' }}>

                        {/* Screenshot Toggle & Drawing Area */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <Checkbox
                                id="include-screenshot-toggle"
                                label="Include screenshot with feedback"
                                isChecked={includeScreenshot}
                                onChange={(_e, checked) => setIncludeScreenshot(checked)}
                            />
                            
                            <div style={{ 
                                border: '2px dashed #ccc', 
                                padding: '5px', 
                                background: '#f0f0f0',
                                opacity: includeScreenshot ? 1 : 0.4,
                                pointerEvents: includeScreenshot ? 'auto' : 'none',
                                transition: 'all 0.3s ease-in-out'
                            }}>
                                <Toolbar inset={{ default: 'insetNone' }}>
                                    <ToolbarContent>
                                        <ToolbarItem>
                                            <PencilAltIcon /> <b>Draw on the image below to highlight issues</b>
                                        </ToolbarItem>
                                        <ToolbarItem align={{ default: 'alignEnd' }}>
                                            <Button variant="control" icon={<EraserIcon />} onClick={clearDrawing}>Reset</Button>
                                        </ToolbarItem>
                                    </ToolbarContent>
                                </Toolbar>

                                <div style={{ overflow: 'auto', maxHeight: '400px', cursor: includeScreenshot ? 'crosshair' : 'not-allowed', textAlign: 'center' }}>
                                    {/* THE CANVAS */}
                                    <canvas
                                        ref={canvasRef}
                                        onMouseDown={startDrawing}
                                        onMouseMove={draw}
                                        onMouseUp={stopDrawing}
                                        onMouseLeave={stopDrawing}
                                        style={{
                                            maxWidth: '100%',
                                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Comment Form */}
                        <Form>
                            {status === 'success' && <Alert variant="success" title="Feedback sent!" isInline />}
                            {status === 'error' && <Alert variant="danger" title="Error sending feedback" isInline />}
                            <FormGroup label="Comments" fieldId="feedback-text">
                                <TextArea
                                    id="feedback-text"
                                    value={comment}
                                    onChange={(_e, v) => setComment(v)}
                                    placeholder="Describe the bug or suggestion..."
                                    rows={3}
                                />
                            </FormGroup>
                        </Form>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button key="confirm" variant="primary" onClick={handleSubmit} isDisabled={isSubmitting}>
                        {isSubmitting ? <Spinner size="sm" /> : 'Send Feedback'}
                    </Button>,
                    <Button key="cancel" variant="link" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                </ModalFooter>
            </Modal>
        </>
    );
};